import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { appShellQueries, routeQueries } from "@/lib/routePrefetch";

/**
 * Start every request the current signed-in route needs, at the moment the
 * session is known (BEN-141 QF6).
 *
 * It is called from `OnboardingGate`, which is the route element for the whole
 * signed-in tree, so it runs on exactly the routes that have a shell and on no
 * others — `/onboarding` and the public tree are outside that element and their
 * traffic is unchanged. Called at the TOP of the gate on purpose: the gate
 * returns `null` (or redirects) while the profile is in flight, so a prefetch
 * mounted anywhere below it cannot run until the very thing it is meant to
 * overlap has already finished.
 *
 * `prefetchQuery` rather than `useQueries`: the descriptors are heterogeneous
 * and their results are read by components far below, so there is nothing for
 * this hook to return and no reason for it to re-render when data lands. It
 * honours each descriptor's stale window, so a navigation between two app
 * routes warms only what has actually gone stale, and it resolves to a cached
 * entry the mounted `useQuery` then joins rather than repeats.
 *
 * Errors are swallowed on purpose. A prefetch is an optimisation: the component
 * that actually needs the data mounts its own query, renders its own error
 * state and owns its own retry. An unhandled rejection here would be a console
 * error for a request the app is already handling twice over.
 */
export function useAppDataPrefetch(enabled: boolean): void {
  const queryClient = useQueryClient();
  const { pathname } = useLocation();

  useEffect(() => {
    if (!enabled) return;
    for (const options of [...appShellQueries(), ...routeQueries(pathname)]) {
      void queryClient.prefetchQuery(options);
    }
  }, [enabled, pathname, queryClient]);
}
